import {assertDataPresent} from '@github-ui/assert-data-present'
import type {FileDiffReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'
import {useFileDiffReference} from '../../use-file-diff-reference'
import type {
  DiffChatWrapper_ComparisonQuery,
  DiffChatWrapper_ComparisonQuery$variables,
} from './__generated__/DiffChatWrapper_ComparisonQuery.graphql'
import type {
  DiffChatWrapper_DiffEntryQuery,
  DiffChatWrapper_DiffEntryQuery$variables,
} from './__generated__/DiffChatWrapper_DiffEntryQuery.graphql'

const WrapperComparisonQuery = graphql`
  query DiffChatWrapper_ComparisonQuery($prId: ID!) {
    pullRequest: node(id: $prId) {
      ... on PullRequest {
        comparison {
          ...useFileDiffReference_Comparison
        }
      }
    }
  }
`

const WrapperDiffEntryQuery = graphql`
  query DiffChatWrapper_DiffEntryQuery($diffEntryId: ID!) {
    diffEntry: node(id: $diffEntryId) {
      ... on PullRequestDiffEntry {
        ...useFileDiffReference_DiffEntry
      }
    }
  }
`

const CopilotCodeChatLoader: React.FC<DiffChatWrapperProps> = ({Component, componentProps, queryVariables}) => {
  const {pullRequest} = useLazyLoadQuery<DiffChatWrapper_ComparisonQuery>(WrapperComparisonQuery, queryVariables)
  const {diffEntry} = useLazyLoadQuery<DiffChatWrapper_DiffEntryQuery>(WrapperDiffEntryQuery, queryVariables)
  assertDataPresent(pullRequest?.comparison)
  assertDataPresent(diffEntry)

  const fileDiffReference = useFileDiffReference(pullRequest.comparison, diffEntry)
  return fileDiffReference ? <Component fileDiffReference={fileDiffReference} {...componentProps} /> : null
}

// prettier-ignore
// it just formats this terribly and it's easier to read like this
export type DiffChatWrapperQueryVariables =
  DiffChatWrapper_ComparisonQuery$variables &
  DiffChatWrapper_DiffEntryQuery$variables

interface DiffChatWrapperProps {
  errorFallback?: React.ReactNode
  suspenseFallback?: React.ReactNode
  queryVariables: DiffChatWrapperQueryVariables
  componentProps?: object // some fancy generic type would be nice here but it's Probably Fine™️
  Component: React.ComponentType<{fileDiffReference: FileDiffReference}>
}

// Wrapper/Loader for components needing a FileDiffReference
export const DiffChatWrapper: React.FC<DiffChatWrapperProps> = ({
  Component,
  componentProps,
  queryVariables,
  errorFallback = null,
  suspenseFallback = null,
}) => (
  <ErrorBoundary fallback={errorFallback}>
    <Suspense fallback={suspenseFallback}>
      <CopilotCodeChatLoader queryVariables={queryVariables} Component={Component} componentProps={componentProps} />
    </Suspense>
  </ErrorBoundary>
)

try{ CopilotCodeChatLoader.displayName ||= 'CopilotCodeChatLoader' } catch {}
try{ DiffChatWrapper.displayName ||= 'DiffChatWrapper' } catch {}